import React, { useState, useContext, useEffect, useLayoutEffect } from 'react';
import { getRolesDefaultItem } from './RoleAPIEndpoints';
import { PageContext } from '../../../lib/PageProvider';
import { RoleGridStateful } from "./RoleGridStateful"
import { RoleFormDialog } from './RoleFormDialog';
import { mutateUrls } from "../../../lib/useGenericSWR";
import { useSWRConfig } from "swr";
import { EnsureUser } from '../../auth/EnsureUser';
import { UcToolbar } from '../../common/UcToolbar';
import { UcListPage } from '../../common/UcListPage';
import { useHasPermission } from 'components/auth/PermissionApiEndpoint';
import AddIcon from '@mui/icons-material/Add';
import GroupsIcon from '@mui/icons-material/Groups';
import { Box, Divider, Drawer } from '@mui/material';
import { UcDrawerHeader } from 'components/common/UcDrawerHeader';
import { RoleDetailsPage } from './RoleDetailsPage';
import { useTranslation } from "react-i18next";

export const RoleListPage = (props) => {
    const { t } = useTranslation();
    const [editDialog, setEditDialog] = useState({ open: false, delete: false, setDefault: false });
    const [selectedItem, setSelectedItem] = useState(null);
    const [pageState, pageDispatch] = useContext(PageContext);
    const [generalSearch, setGeneralSearch] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [reMutateUrls, setReMutateUrls] = useState(false);
    const swrConfig = useSWRConfig();

    const closeHandler = () => {
        setEditDialog({ open: false, itemToEdit: editDialog.dataItem });
    }

    const saveHandler = () => {
        closeHandler();

        // Also, remutate
        setReMutateUrls(true);
    }

    const openDialog = (dataItem) => {
        if (!dataItem) {
            return;
        }

        if (!dataItem.formIteration) {
            dataItem.formIteration = "edit" + dataItem.id;
        }

        setEditDialog({ open: true, itemToEdit: dataItem, delete: false, setDefault: false });
    }

    const deleteDialog = (dataItem) => {
        if (!dataItem) {
            return;
        }
        if (!dataItem.formIteration) {
            dataItem.formIteration = "delete" + dataItem.id;
        }
        setEditDialog({ open: true, itemToEdit: dataItem, delete: true, setDefault: false });
    }

    const setDefaultDialog = (dataItem) => {
        if (!dataItem) {
            return;
        }

        if (!dataItem.formIteration) {
            dataItem.formIteration = "setDefault" + dataItem.id;
        }

        setEditDialog({ open: true, itemToEdit: dataItem, delete: false, setDefault: true });
    }

    const createItem = () => {
        openDialog(getRolesDefaultItem());
    }

    const editItem = (item) => {
        openDialog(item);
        handleDrawerClose();
    }

    const deleteItem = (item) => {
        deleteDialog(item);
        handleDrawerClose();
    }

    const setIsDefault = () => {
        setDefaultDialog(selectedItem);
    }

    const canSetIsDefault = (role) => {
        return (role?.roleType?.toLowerCase() === 'custom' || role?.roleType?.toLowerCase() === 'utilityresidential');
    }

    const actions = [
        {
            icon: <AddIcon />,
            title: t('Add Role'),
            toolTip: t('Add Role'),
            onClick: () => { createItem(); },
            isHidden: !useHasPermission("Role:Modify"),
            isDisabled: false
        },
        {
            icon: <GroupsIcon />,
            title: t('Set Selected as Default'),
            toolTip: t('Set Selected as Default User Role'),
            onClick: () => { setIsDefault(); },
            isHidden: !useHasPermission("RoleDefault:Modify"),
            isDisabled: ((selectedItem !== null && canSetIsDefault(selectedItem)) ? false : true),
            color: 'secondary'
        }
    ];

    useLayoutEffect(() => {
        if (reMutateUrls) {
            mutateUrls(swrConfig);
            setReMutateUrls(false);
        }
    }, [reMutateUrls, swrConfig]);

    useEffect(() => {
        pageDispatch({
            header: "Roles",
        });
    }, [pageState.header, pageDispatch]);

    const handleDrawerClose = () => {
        setDrawerOpen(false);
    }

    const doubleClick = () => {
        if (selectedItem !== null && selectedItem?.selectedId !== 0 && !editDialog.open) {
            setDrawerOpen(true);
        }
    };
    const drawerWidth = "50vw";

    console.log("RoleListPage - Loaded");
    return (
        <UcListPage>
            <EnsureUser permissions={["Role:Read"]}>
                <h1 className="pageHeader">{t("Roles")}</h1>
                <UcToolbar
                    hasGeneralSearch={true}
                    onGeneralSearchChange={(value) => {
                        setGeneralSearch(value);
                    } }
                    actions={actions}>
                </UcToolbar>
                <RoleFormDialog
                    open={editDialog.open}
                    delete={editDialog.delete}
                    setDefault={editDialog.setDefault}
                    itemToEdit={editDialog.itemToEdit}
                    onCancel={closeHandler}
                    onSave={saveHandler}
                />
                {!reMutateUrls &&
                    <RoleGridStateful
                        setDrawerOpen={setDrawerOpen}
                        onEditItem={editItem}
                        onDeleteItem={deleteItem}
                        generalSearch={generalSearch}
                        doubleClick={doubleClick}
                        onSelected={(roleGridSelectedItem) => {
                            setSelectedItem({ ...roleGridSelectedItem });
                        }} />
                }

                <Drawer
                    anchor="right"
                    open={drawerOpen}
                    onClose={handleDrawerClose}
                >
                    <UcDrawerHeader justify="flex-start"/>
                    <Box
                        sx={{ width: drawerWidth, margin: '10px' }}
                        role="presentation"
                    >
                        <Divider />
                        {selectedItem &&
                            <RoleDetailsPage roleId={selectedItem.id} />
                        }
                    </Box>
                </Drawer>
            </EnsureUser>
        </UcListPage>
    );
}